import React, { useState, useEffect } from "react";
import "./BeweissicherungPage.css"; 
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import html2pdf from "html2pdf.js";

/* --- IMPORTAI --- */
import { db } from '../firebase'; 
import { collection, query, where, getDocs, orderBy, deleteDoc, updateDoc, doc, getDoc } from 'firebase/firestore';
import { getAuth, onAuthStateChanged } from "firebase/auth";

/* --- LOGO --- */
import bfLogo from '../assets/bf_logo.png';

/* --- IKONOS --- */
const IconBack = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="19" y1="12" x2="5" y2="12"></line><polyline points="12 19 5 12 12 5"></polyline></svg>;
const IconCalendar = () => <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>;
const IconMapPin = () => <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#38bdf8" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path><circle cx="12" cy="10" r="3"></circle></svg>;
const IconClose = () => <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>;
const IconDownload = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>;
const IconSearch = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>;
const IconImage = () => <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>;
const IconTrash = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>;

const formatAddress = (addr) => {
    if (!addr) return "";
    if (typeof addr === 'object') {
        return `${addr.street || ''} ${addr.city || ''} ${addr.zip || ''}`;
    }
    return addr;
};

// --- PAGALBINĖ FUNKCIJA LAIKUI IŠ URL (ATSARGINIS VARIANTAS) ---
const formatTimeFromUrl = (url) => {
    try {
        const match = url.match(/\/(\d{13})_/); 
        if (match && match[1]) {
            const date = new Date(parseInt(match[1]));
            // Formatas: DD.MM.YYYY HH:mm
            const d = String(date.getDate()).padStart(2, '0');
            const m = String(date.getMonth() + 1).padStart(2, '0');
            const y = date.getFullYear();
            const hh = String(date.getHours()).padStart(2, '0');
            const mm = String(date.getMinutes()).padStart(2, '0');
            return `${d}.${m}.${y} ${hh}:${mm}`;
        }
    } catch (e) { return null; }
    return null;
};

const BeweissicherungPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  const [reports, setReports] = useState([]); 
  const [selectedReport, setSelectedReport] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [loading, setLoading] = useState(true);
  
  const [companyInfo, setCompanyInfo] = useState({ name: "", address: "" });

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (user) => {
        if (user) {
            fetchReports(user.uid);
            fetchCompanyInfo(user.uid);
        } else {
            setLoading(false);
        }
    });
    return () => unsubscribe(); 
  }, []);

  const fetchCompanyInfo = async (uid) => {
      try {
          const userDoc = await getDoc(doc(db, "users", uid));
          if (userDoc.exists()) {
              const userData = userDoc.data();
              setCompanyInfo({
                  name: userData.companyName || "",
                  address: formatAddress(userData.address)
              });
          }
      } catch (err) {
          console.error("Klaida gaunant įmonės info:", err);
      }
  };

  const fetchReports = async (uid) => {
      try {
        const q = query(
            collection(db, "project_reports"),
            where("adminId", "==", uid),
            orderBy("timestamp", "desc")
        );

        const querySnapshot = await getDocs(q);
        const fetchedReports = querySnapshot.docs.map(doc => {
            const data = doc.data();
            let photos = [];
            
            // --- SVARBU: Čia paimame naujus laukus (uploadedAt, isArchive) iš DB ---
            if (data.allImages && Array.isArray(data.allImages)) {
                photos = data.allImages; 
            } else if (data.imageUrl) {
                // Senas formatas (be masyvo)
                photos = [{ url: data.imageUrl, comment: data.comment || "" }];
            }

            return {
                id: doc.id,
                workerName: data.workerName || t('unknown_worker') || "Unknown",
                projectName: data.projectName || t('unknown_project') || "Project", 
                address: formatAddress(data.address),
                customerName: data.customerName || "",
                timestamp: data.timestamp,
                status: data.status || "new",
                signature: data.signatureUrl,
                clientSignature: data.clientSignatureUrl || null,
                photos: photos,
                generalComment: data.comment 
            };
        });

        setReports(fetchedReports);
      } catch (error) {
        console.error("Error fetching reports:", error);
      } finally {
        setLoading(false);
      }
  };

  const handleViewReport = async (report) => {
      setSelectedReport(report);
      if (report.status === 'new') {
          try {
              const reportRef = doc(db, "project_reports", report.id);
              await updateDoc(reportRef, { status: 'viewed' });
              setReports(prev => prev.map(r => r.id === report.id ? { ...r, status: 'viewed' } : r));
          } catch (e) {
              console.error("Klaida atnaujinant statusą:", e);
          }
      }
  };

  const handleDeleteReport = async (e, reportId) => {
      e.stopPropagation(); 
      const message = t('confirm_delete') || "Delete?";
      const confirmed = window.confirm(message);
      if (!confirmed) return;

      try {
          await deleteDoc(doc(db, "project_reports", reportId));
          setReports(prevReports => prevReports.filter(report => report.id !== reportId));
      } catch (error) {
          console.error("Klaida trinant:", error);
          alert("Error deleting.");
      }
  };

  /* --- PDF GENERAVIMAS --- */
  const handleDownloadPDF = () => {
      setIsGenerating(true);
      const element = document.getElementById('pdf-content');
      const filename = `Report_${selectedReport.workerName}_${new Date().toISOString().split('T')[0]}.pdf`;

      const opt = {
        margin:       [10, 10, 15, 10], 
        filename:     filename,
        image:        { type: 'jpeg', quality: 0.98 },
        html2canvas:  { scale: 2, useCORS: true, letterRendering: true }, 
        jsPDF:        { unit: 'mm', format: 'a4', orientation: 'portrait' },
        pagebreak:    { mode: ['avoid-all', 'css', 'legacy'] } 
      };
      
      html2pdf().set(opt).from(element).toPdf().get('pdf').then((pdf) => {
          const totalPages = pdf.internal.getNumberOfPages();
          
          for (let i = 1; i <= totalPages; i++) {
              pdf.setPage(i);
              pdf.setFontSize(10);
              pdf.setTextColor(100);
              
              const pageText = `${t('page') || 'Page'} ${i} / ${totalPages}`;
              
              const pageWidth = pdf.internal.pageSize.getWidth();
              const pageHeight = pdf.internal.pageSize.getHeight();
              
              pdf.text(pageText, pageWidth - 25, pageHeight - 10);
          }
      }).save().then(() => setIsGenerating(false));
  };

  const filteredReports = reports.filter((report) => {
    const term = searchTerm.toLowerCase();
    
    // Čia formatuojame datą paieškai
    let dateStr = "";
    if (report.timestamp?.toDate) {
        const d = report.timestamp.toDate();
        const day = String(d.getDate()).padStart(2, '0');
        const mon = String(d.getMonth() + 1).padStart(2, '0');
        const yr = d.getFullYear();
        dateStr = `${day}.${mon}.${yr}`;
    }

    const searchString = `
        ${report.workerName}
        ${report.projectName}
        ${report.address}
        ${dateStr}
    `.toLowerCase();

    return searchString.includes(term);
  });

  // --- DATA FORMATAVIMAS HEADER'iui (DD.MM.YYYY HH:mm) ---
  const formatHeaderDate = (timestamp) => {
      if (!timestamp?.toDate) return "";
      const d = timestamp.toDate();
      const day = String(d.getDate()).padStart(2, '0');
      const mon = String(d.getMonth() + 1).padStart(2, '0');
      const yr = d.getFullYear();
      const hh = String(d.getHours()).padStart(2, '0');
      const mm = String(d.getMinutes()).padStart(2, '0');
      return `${day}.${mon}.${yr} ${hh}:${mm}`;
  };

  return (
    <div className="weekly-container">
      <div className="weekly-content">
        
        {/* HEADERIS */}
        <div className="beweis-header-column">
            <div className="header-back-btn-container">
                <button className="back-btn" onClick={() => navigate('/admin')}>
                    <IconBack /> {t('back') || "Back"}
                </button>
            </div>
            
            <img src={bfLogo} alt="BuildForce" className="header-logo" />
            <h1 className="weekly-title">{t('reports_title') || "REPORTS"}</h1>
        </div>

        {/* PAIEŠKA */}
        <div className="search-container">
            <div className="search-box">
                <IconSearch />
                <input 
                    type="text" 
                    placeholder={t('search') || "Search..."} 
                    value={searchTerm} 
                    onChange={(e) => setSearchTerm(e.target.value)} 
                />
                {searchTerm && <button className="clear-search" onClick={() => setSearchTerm("")}>✕</button>}
            </div>
        </div>

        {loading ? (
            <div style={{textAlign: "center", padding: "20px", color: "#888"}}>{t('loading') || "Loading..."}</div>
        ) : (
            <div className="beweis-grid">
                {filteredReports.length === 0 ? (
                    <div style={{gridColumn: "1/-1", textAlign:"center", padding:20, color:"#999"}}>
                        {t('no_reports') || "No reports found."}
                    </div>
                ) : (
                    filteredReports.map((report) => (
                    <div key={report.id} className="beweis-card" onClick={() => handleViewReport(report)}>
                        <div className="beweis-card-header">
                            <div className="b-worker-info">
                                <span className="b-worker-name">{report.workerName}</span>
                                <span className="b-date">
                                    <span style={{marginRight:4, display:'inline-flex'}}><IconCalendar/></span>
                                    {formatHeaderDate(report.timestamp)}
                                </span>
                            </div>
                            
                            <div style={{display:'flex', alignItems:'center', gap:'10px'}}>
                                <div className={`b-status ${
                                    report.status === 'new' ? 'st-new' : 
                                    report.status === 'completed' ? 'st-completed' : 'st-viewed'
                                }`}>
                                    {report.status === 'new' ? (t('status_new') || "New") : 
                                     report.status === 'completed' ? (t('status_completed') || "Completed") : 
                                     (t('status_viewed') || "Viewed")}
                                </div>
                                
                                <button 
                                    className="delete-icon-btn"
                                    onClick={(e) => handleDeleteReport(e, report.id)}
                                    title={t('delete') || "Delete"}
                                    style={{background:'transparent', border:'none', cursor:'pointer'}}
                                >
                                    <IconTrash />
                                </button>
                            </div>
                        </div>

                        <div className="card-gallery-preview">
                            {report.photos && report.photos.slice(0, 3).map((photo, index) => (
                                <div key={index} className="preview-thumb">
                                    <img src={photo.url} alt="Foto" />
                                </div>
                            ))}
                            {report.photos && report.photos.length > 3 && (
                                <div className="preview-more">
                                    +{report.photos.length - 3}
                                </div>
                            )}
                            {(!report.photos || report.photos.length === 0) && (
                                <div className="no-img-text">{t('no_photos') || "No photos"}</div>
                            )}
                        </div>
                        <div className="photo-count-badge">
                            <IconImage /> <span>{report.photos ? report.photos.length : 0}</span>
                        </div>

                        <div className="beweis-footer">
                            <div className="b-location">
                                <span style={{marginRight:4, display:'inline-flex'}}><IconMapPin/></span>
                                <span>{report.projectName}</span>
                            </div>
                            <button className="mini-btn-blue">
                                {t('view_all') || "View all"}
                            </button>
                        </div>
                    </div>
                    ))
                )}
            </div>
        )}
      </div>

      {selectedReport && (
        <div className="modal-overlay">
            {/* SCROLL YRA ČIA */}
            <div className="modal-content" style={{ maxHeight: '90vh', overflowY: 'auto' }}>
                
                <div className="modal-actions">
                    <button className="print-btn" onClick={handleDownloadPDF} disabled={isGenerating}>
                        <IconDownload /> {isGenerating ? (t('generating') || "Generating...") : (t('download_pdf') || "Download PDF")}
                    </button>
                    <button className="modal-close-btn" onClick={() => setSelectedReport(null)}>
                        <IconClose />
                    </button>
                </div>

                {/* PDF TEMPLATE */}
                <div id="pdf-content" style={{
                    width: '100%', 
                    backgroundColor: '#ffffff', 
                    color: '#000000', 
                    padding: '40px', 
                    fontFamily: 'Arial, sans-serif', 
                    fontSize: '16px',
                    lineHeight: '1.5'
                }}>
                    {/* Header */}
                    <div style={{textAlign: 'center', marginBottom: '30px', borderBottom: '2px solid #000', paddingBottom: '20px'}}>
                        <h2 style={{margin: '0', fontSize:'24px', textTransform: 'uppercase', fontWeight: 'bold', color: '#000'}}>
                            {companyInfo.name || "COMPANY NAME"}
                        </h2>
                        <p style={{margin: '10px 0 0 0', fontSize: '14px', color: '#000'}}>
                            {companyInfo.address}
                        </p>
                    </div>

                    {/* Info Table */}
                    <div className="pdf-avoid-break" style={{marginBottom: '30px', border: '1px solid #ccc', padding: '15px', borderRadius: '5px', background: '#f9f9f9'}}>
                        <table style={{width: '100%', borderCollapse: 'collapse', color: '#000'}}>
                            <tbody>
                                <tr>
                                    <td style={{padding: '5px', fontWeight: 'bold', width: '150px'}}>{t('worker') || "Worker"}:</td>
                                    <td style={{padding: '5px'}}>{selectedReport.workerName}</td>
                                </tr>
                                <tr>
                                    <td style={{padding: '5px', fontWeight: 'bold'}}>{t('project') || "Project"}:</td>
                                    <td style={{padding: '5px'}}>{selectedReport.projectName}</td>
                                </tr>
                                <tr>
                                    <td style={{padding: '5px', fontWeight: 'bold'}}>{t('address') || "Address"}:</td>
                                    <td style={{padding: '5px'}}>{selectedReport.address || "-"}</td>
                                </tr>
                                {selectedReport.customerName && (
                                    <tr>
                                        <td style={{padding: '5px', fontWeight: 'bold'}}>{t('client') || "Client"}:</td>
                                        <td style={{padding: '5px'}}>{selectedReport.customerName}</td>
                                    </tr>
                                )}
                                <tr>
                                    <td style={{padding: '5px', fontWeight: 'bold'}}>{t('date') || "Date"}:</td>
                                    <td style={{padding: '5px'}}>{formatHeaderDate(selectedReport.timestamp)}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    {/* Photos */}
                    <div style={{display: 'flex', flexDirection: 'column', gap: '30px', marginBottom: '30px'}}>
                        {selectedReport.photos && selectedReport.photos.map((photo, index) => {
                            // --- ATKŪRIMAS: Tikriname informaciją, kurią siuntė Workeris ---
                            let timeLabel = "";
                            if (photo.uploadedAt) {
                                // TIKSLUS LAIKAS (Iš kameros)
                                timeLabel = photo.uploadedAt; 
                            } else if (photo.isArchive) {
                                // ARCHYVAS (Iš galerijos)
                                timeLabel = t('from_archive') || "(From Archive)";
                            } else {
                                // ATSARGINIS (Jei senas raportas)
                                const fromUrl = formatTimeFromUrl(photo.url);
                                if(fromUrl) timeLabel = fromUrl;
                            }
                            // ----------------------------------------

                            return (
                            <div key={index} className="pdf-avoid-break" style={{
                                border: '1px solid #000', 
                                borderRadius: '5px', 
                                background: '#fff', 
                                overflow: 'hidden',
                                pageBreakInside: 'avoid',
                                breakInside: 'avoid'
                            }}>
                                <div style={{
                                    width: '100%',
                                    backgroundColor: '#f5f5f5',
                                    borderBottom: '1px solid #000'
                                }}>
                                    <img 
                                        src={photo.url} 
                                        alt={`Foto ${index+1}`}
                                        crossOrigin="anonymous" 
                                        style={{
                                            width: '100%',
                                            height: 'auto',
                                            display: 'block'
                                        }}
                                    />
                                </div>

                                <div style={{padding: '10px'}}>
                                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                                        <p style={{margin: '0', fontWeight: 'bold', fontSize: '14px', color: '#000'}}>{t('photo_num') || "Photo"} #{index + 1}</p>
                                        
                                        {/* --- RODYTI LAIKĄ ARBA ARCHYVO ŽYMĄ --- */}
                                        {timeLabel && (
                                            <span style={{fontSize: '12px', color: '#555', fontStyle: 'italic'}}>
                                                {timeLabel}
                                            </span>
                                        )}
                                        {/* --------------------------------------- */}
                                    </div>

                                    {photo.comment && (
                                        <p style={{margin: '5px 0 0 0', fontStyle: 'italic', fontSize: '14px', color: '#000'}}>
                                            {photo.comment}
                                        </p>
                                    )}
                                </div>
                            </div>
                        )})}
                        {(!selectedReport.photos || selectedReport.photos.length === 0) && (
                            <div style={{textAlign:'center', padding: '20px', color: '#000'}}>{t('no_photos_in_record') || "No photos."}</div>
                        )}
                    </div>

                    {/* General Comment */}
                    <div className="pdf-avoid-break" style={{
                        marginBottom: '30px', 
                        backgroundColor: '#f4f4f4', 
                        padding: '15px', 
                        borderRadius: '5px', 
                        border: '1px solid #000',
                        pageBreakInside: 'avoid',
                        breakInside: 'avoid'
                    }}>
                        <p style={{fontWeight: 'bold', margin: '0 0 10px 0', textTransform: 'uppercase', fontSize: '14px', color: '#000'}}>
                            {t('general_comment_uppercase') || "GENERAL COMMENT"}:
                        </p>
                        <p style={{margin: 0, fontSize: '16px', whiteSpace: 'pre-wrap', color: '#000'}}>
                            {selectedReport.generalComment || (t('no_comment') || "-")}
                        </p>
                    </div>

                    {/* Signatures */}
                    <div className="pdf-avoid-break" style={{
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        marginTop: '40px', 
                        borderTop: '2px solid #000', 
                        paddingTop: '20px',
                        pageBreakInside: 'avoid',
                        breakInside: 'avoid'
                    }}>
                        
                        {/* Worker Signature */}
                        <div style={{textAlign: 'center', width: '200px'}}>
                            {selectedReport.signature ? (
                                <img src={selectedReport.signature} alt="Worker Sig" crossOrigin="anonymous" style={{height: '60px', marginBottom: '10px'}} />
                            ) : (
                                <div style={{height:'60px'}}></div>
                            )}
                            <div style={{borderTop: '1px solid #000', paddingTop: '5px', fontSize: '12px', color: '#000', fontWeight: 'bold'}}>
                                {t('signature') || "Worker Signature"}
                            </div>
                        </div>

                        {/* Client Signature */}
                        {selectedReport.clientSignature && (
                            <div style={{textAlign: 'center', width: '200px'}}>
                                <img src={selectedReport.clientSignature} alt="Client Sig" crossOrigin="anonymous" style={{height: '60px', marginBottom: '10px'}} />
                                <div style={{borderTop: '1px solid #000', paddingTop: '5px', fontSize: '12px', color: '#000', fontWeight: 'bold'}}>
                                    {t('verified_by') || "Verified by"}
                                </div>
                            </div>
                        )}
                    </div>

                    <div style={{textAlign: 'center', marginTop: '40px', fontSize: '10px', color: '#555'}}>
                        <p>{t('generated_by_system') || "Generated by BuildForce AL"}</p>
                    </div>
                </div>
            </div>
        </div>
      )}
    </div>
  );
};

export default BeweissicherungPage;